(() => {
  if (customElements.get('giorgio-product')) return;
  class GiorgioProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.state = JSON.parse(this.querySelector('[data-g-product-state]').textContent);
      this.labels = this.state.labels;
      this.form = this.querySelector('form.g-product-form');
      this.form.noValidate = true;
      this.variantInput = this.form.querySelector('[data-variant-id]');
      this.variantInput.name = 'id';
      this.optionGroups = [...this.querySelectorAll('[data-option-position]')];
      this.quantity = this.form.elements.quantity;
      this.submit = this.querySelector('[data-product-submit]');
      this.submitLabel = this.querySelector('[data-product-submit-label]');
      this.sticky = this.querySelector('[data-purchase-bar]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.error = this.querySelector('[data-form-error]');
      this.preorderMode = this.dataset.preorderMode || 'off';
      this.preorderPreview = this.dataset.preorderPreview === 'true';
      this.slides = [...this.querySelectorAll('[data-media-id]')];
      this.thumbnails = [...this.querySelectorAll('[data-thumbnail]')];
      this.index = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => this.onChange(event));
      this.addEventListener('keydown', event => this.onKeydown(event));
      this.form.addEventListener('submit', event => this.onSubmit(event));
      this.addEventListener('input', () => { this.clearAppValidation(); this.updateSummary(); });
      this.onAppValidationSuccess = event => this.acceptAppValidation(event);
      this.onAppValidationFail = event => {
        const element = event.detail?.element?.[0] || event.detail?.element;
        if (element?.closest?.('form') === this.form) {
          this.clearAppValidation();
          if (this.suitSelector?.reveal(element)) {
            const field = element.matches?.('input,select,textarea,button') ? element : element.querySelector?.('input:not(:disabled),select:not(:disabled),textarea:not(:disabled)');
            field?.focus();
          }
        }
      };
      document.addEventListener('infiniteoptions.validationSuccess', this.onAppValidationSuccess);
      document.addEventListener('infiniteoptions.validationFail', this.onAppValidationFail);
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
          if (this.dialogOpener?.isConnected) this.dialogOpener.focus();
          this.updateSticky();
        });
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        });
      });
      this.viewport = matchMedia('(max-width:989px)');
      this.onViewportChange = () => this.updateSticky();
      this.viewport.addEventListener('change', this.onViewportChange);
      this.onStickyScroll = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = 0;
          this.updateSticky();
        });
      };
      window.addEventListener('scroll', this.onStickyScroll, {passive: true});
      window.addEventListener('resize', this.onStickyScroll, {passive: true});
      this.intersectionObserver = new IntersectionObserver(() => this.updateSticky());
      this.intersectionObserver.observe(this.submit);
      this.resizeObserver = new ResizeObserver(() => this.updateStickyClearance());
      this.resizeObserver.observe(this.sticky);
      this.appOptions = this.querySelector('.g-product-app-options');
      if (this.appOptions) {
        this.repairAppGroupLabels();
        this.appLabelObserver = new MutationObserver(() => this.repairAppGroupLabels());
        this.appLabelObserver.observe(this.appOptions, {subtree: true, childList: true, attributes: true, attributeFilter: ['aria-labelledby', 'id']});
      }
      this.suitSelector = window.GiorgioSuitSelector?.attach(this);
      this.onHistoryChange = () => { this.suitSelector?.historyChanged(); this.resolveVariant(false, true); };
      window.addEventListener('popstate', this.onHistoryChange);
      this.onPageShow = () => {
        this.clearAppValidation();
        this.externalSubmission = false;
        if (this.querySelector('[data-g-preorder]')?.dataset.mode === 'confirmed') this.resolveVariant(false, true);
        else this.updateControls();
      };
      window.addEventListener('pageshow', this.onPageShow);
      const stage = this.querySelector('[data-gallery-stage]');
      stage?.addEventListener('touchstart', event => {
        this.touchStart = {x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY};
      }, {passive: true});
      stage?.addEventListener('touchend', event => {
        if (!this.touchStart || event.target.closest('video,model-viewer,iframe')) return;
        const dx = event.changedTouches[0].clientX - this.touchStart.x;
        const dy = event.changedTouches[0].clientY - this.touchStart.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.selectImage(this.index + (dx < 0 ? 1 : -1));
        this.touchStart = null;
      }, {passive: true});
      this.classList.add('is-enhanced');
      this.querySelector('[data-preorder-nojs-plan-disabled]')?.removeAttribute('disabled');
      this.updateControls();
      this.selectImage(this.index, false);
    }
    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.appLabelObserver?.disconnect();
      this.suitSelector?.destroy();
      this.clearAppValidation();
      document.removeEventListener('infiniteoptions.validationSuccess', this.onAppValidationSuccess);
      document.removeEventListener('infiniteoptions.validationFail', this.onAppValidationFail);
      this.variantAbort = null;
      this.viewport?.removeEventListener('change', this.onViewportChange);
      window.removeEventListener('popstate', this.onHistoryChange);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('scroll', this.onStickyScroll);
      window.removeEventListener('resize', this.onStickyScroll);
      cancelAnimationFrame(this.stickyFrame);
      document.body.classList.remove('g-has-purchasebar');
      clearTimeout(this.externalTimeout);
    }
    completeSelection() {
      return this.optionGroups.every(group => group.querySelector('input:checked')) && (!this.suitSelector || this.suitSelector.complete());
    }
    setText(node, text) {
      if (node && node.textContent !== text) node.textContent = text;
    }
    setFieldError(error, message, fields = [], markInvalid = true) {
      if (!error) return;
      const invalid = Boolean(message);
      if (!invalid) error.hidden = true;
      this.setText(error, message || '');
      fields.filter(Boolean).forEach(field => {
        if (markInvalid) {
          if (invalid) {
            if (field.getAttribute('aria-invalid') !== 'true') field.setAttribute('aria-invalid', 'true');
          } else field.removeAttribute('aria-invalid');
        }
        if (!error.id) return;
        const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== error.id);
        if (invalid) ids.push(error.id);
        const description = ids.join(' ');
        if (description) {
          if (field.getAttribute('aria-describedby') !== description) field.setAttribute('aria-describedby', description);
        } else field.removeAttribute('aria-describedby');
      });
      if (invalid) error.hidden = false;
    }
    repairAppGroupLabels() {
      this.appOptions?.querySelectorAll('[role="group"][aria-labelledby]').forEach(group => {
        const reference = group.getAttribute('aria-labelledby');
        if (!reference || !/\s/.test(reference)) return;
        const tokens = reference.trim().split(/\s+/);
        if (tokens.every(id => document.getElementById(id))) return;
        const originalLabel = document.getElementById(reference);
        const label = originalLabel?.textContent.replace(/\s+/g, ' ').trim();
        if (!label) return;
        group.setAttribute('aria-label', label);
        group.removeAttribute('aria-labelledby');
      });
    }
    onClick(event) {
      const thumbnail = event.target.closest('[data-thumbnail]');
      if (thumbnail) this.selectImage(this.slides.findIndex(slide => slide.dataset.mediaId === thumbnail.dataset.thumbnail));
      const direction = event.target.closest('[data-gallery-direction]');
      if (direction) this.selectImage(this.index + Number(direction.dataset.galleryDirection));
      const quantityButton = event.target.closest('[data-quantity-change]');
      if (quantityButton) {
        const value = Number(this.quantity.value) || Number(this.quantity.min) || 1;
        const delta = Number(quantityButton.dataset.quantityChange) * (Number(this.quantity.step) || 1);
        const next = Math.max(Number(this.quantity.min) || 1, value + delta);
        this.quantity.value = this.quantity.max ? Math.min(Number(this.quantity.max), next) : next;
        this.quantity.dispatchEvent(new Event('change', {bubbles: true}));
      }
      const size = event.target.closest('[data-open-size]');
      if (size) this.openDialog(this.querySelector('[data-size-dialog]'), size);
      const zoom = event.target.closest('[data-open-zoom]');
      if (zoom) {
        const current = this.slides[this.index]?.querySelector('img');
        const image = this.querySelector('[data-zoom-image]');
        if (current) {
          const url = new URL(current.currentSrc || current.src);
          url.searchParams.set('width', '2000');
          image.src = url.toString();
          image.alt = current.alt;
          image.width = current.naturalWidth || current.width;
          image.height = current.naturalHeight || current.height;
          this.openDialog(this.querySelector('[data-zoom-dialog]'), zoom);
        }
      }
      const close = event.target.closest('[data-close-dialog]');
      if (close) close.closest('dialog').close();
      if (event.target.closest('[data-sticky-submit]')) this.submit.click();
      if (event.target.closest('[data-continue-shopping]')) {
        this.querySelector('[data-cart-result]').hidden = true;
        const firstOption = this.optionGroups[0]?.querySelector('input:checked,input:not(:disabled)');
        if (firstOption) { this.suitSelector?.reveal(firstOption); firstOption.focus(); }
        if (!this.optionGroups.length) this.quantity.focus();
      }
    }
    onChange(event) {
      this.clearAppValidation();
      if (event.target.matches('[data-option-value-id]')) {
        const group = event.target.closest('[data-option-position]');
        group.classList.remove('has-error');
        this.setFieldError(group.querySelector('[data-option-error]'), '', [group, ...group.querySelectorAll('input')]);
        this.setFieldError(this.error, '', [this.submit], false);
        this.resolveVariant();
      } else {
        if (event.target.matches('[name="selling_plan"]')) {
          const consent = this.querySelector('[data-preorder-consent]');
          if (consent) consent.checked = false;
          this.setFieldError(this.querySelector('[data-preorder-error]'), '', [consent]);
          this.setFieldError(this.error, '', [this.submit], false);
          if (this.completeSelection()) this.resolveVariant(false);
          else this.updateControls();
          return;
        }
        if (event.target === this.quantity && this.quantity.checkValidity() && Number.isSafeInteger(Number(this.quantity.value))) {
          this.setFieldError(this.querySelector('[data-quantity-error]'), '', [this.quantity]);
          this.setFieldError(this.error, '', [this.submit], false);
        }
        if (this.suitSelector?.enhanced && !this.suitSelector.restoring && this.completeSelection() && !this.state.variant && !this.resolving && !this.variantError) this.resolveVariant(false);
        else this.updateControls();
      }
    }
    onKeydown(event) {
      const thumbnail = event.target.closest('[data-thumbnail]');
      if (thumbnail && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const index = this.thumbnails.indexOf(thumbnail);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.thumbnails.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.thumbnails.length) % this.thumbnails.length;
        this.thumbnails[next].focus();
        this.selectImage(next);
      }
      const dialog = event.target.closest('dialog[open]');
      if (dialog && event.key === 'Tab') {
        const focusable = [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex="0"]')].filter(element => element.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    openDialog(dialog, opener) {
      this.dialogOpener = opener;
      dialog.showModal();
      dialog.querySelector('[data-close-dialog]')?.focus();
      this.updateSticky();
    }
    selectImage(index, announce = true) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.index;
        if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      this.thumbnails.forEach((thumbnail, i) => {
        if (i === this.index) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      const status = this.querySelector('[data-gallery-status]');
      const text = this.labels.imageCount.replace('__NUMBER__', String(this.index + 1)).replace('__TOTAL__', String(this.slides.length));
      if (!announce) status.setAttribute('aria-live', 'off');
      status.textContent = text;
      if (!announce) requestAnimationFrame(() => status.setAttribute('aria-live', 'polite'));
      this.querySelector('[data-open-zoom]').hidden = this.slides[this.index].dataset.mediaType !== 'image';
    }
    async resolveVariant(pushHistory = true, useLocation = false, preserveAcknowledgement = false) {
      if (this.suitSelector?.requiresConfirmation?.()) {
        // A missing same-jacket inventory reference cannot authorize a purchase.
        this.variantAbort = null;
        this.state.variant = null;
        this.variantInput.value = '';
        this.variantError = false;
        this.resolving = false;
        this.clearAppValidation();
        this.setFieldError(this.error, '', [this.submit], false);
        this.updateControls();
        return;
      }
      const oldPreorder = this.preorderInfo();
      const acknowledged = preserveAcknowledgement && this.querySelector('[data-preorder-consent]')?.checked;
      // Shared app fetch wrappers can leave rejected branches when a request is aborted.
      // Let old reads finish; request identity below prevents stale commits and error updates.
      const controller = new AbortController();
      this.variantAbort = controller;
      this.state.variant = null;
      this.variantInput.value = '';
      this.variantError = false;
      this.resolving = true;
      this.setText(this.querySelector('[data-variant-status]'), this.labels.optionsLoading);
      this.updateControls();
      const url = new URL(location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      if (!useLocation) {
        url.searchParams.delete('variant');
        const requested = this.suitSelector?.enhanced ? this.suitSelector.requestInputs() : null;
        const values = (requested || this.optionGroups.map(group => group.querySelector('input:checked'))).map(input => input?.dataset.optionValueId).filter(Boolean);
        if (values.length) url.searchParams.set('option_values', values.join(','));
        else url.searchParams.delete('option_values');
      }
      try {
        const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin', headers: {'Accept':'text/html'}});
        if (!response.ok) throw new Error(this.labels.selectionError);
        const html = await response.text();
        if (this.variantAbort !== controller || controller.signal.aborted) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const fragment = documentFragment.querySelector('giorgio-product');
        if (!fragment || fragment.dataset.productId !== this.dataset.productId || fragment.dataset.preorderMode !== this.dataset.preorderMode || fragment.dataset.preorderPreview !== this.dataset.preorderPreview) throw new Error(this.labels.selectionError);
        const next = JSON.parse(fragment.querySelector('[data-g-product-state]').textContent);
        if (typeof next.available !== 'boolean') throw new Error(this.labels.selectionError);
        const renderedOptions = new Map([...fragment.querySelectorAll('[data-option-value-id]')].map(input => [input.dataset.optionValueId, input]));
        const currentOptions = this.optionGroups.flatMap(group => [...group.querySelectorAll('[data-option-value-id]')]);
        if (currentOptions.some(input => !renderedOptions.has(input.dataset.optionValueId))) throw new Error(this.labels.selectionError);
        this.state.available = next.available;
        currentOptions.forEach(input => {
          const rendered = renderedOptions.get(input.dataset.optionValueId);
          if (rendered.hasAttribute('data-unavailable')) input.dataset.unavailable = 'true';
          else delete input.dataset.unavailable;
          const note = input.parentElement?.querySelector('.g-product-unavailable-note');
          const renderedNote = rendered.parentElement?.querySelector('.g-product-unavailable-note');
          if (note) note.textContent = renderedNote ? renderedNote.textContent : '';
          if (useLocation) input.checked = rendered.checked;
        });
        if (useLocation) this.suitSelector?.restoreUrlWaist();
        const complete = this.completeSelection();
        const variant = complete ? next.variant : null;
        if (variant && this.suitSelector?.enhanced && !this.suitSelector.matchesFulfillment(variant)) throw new Error(this.labels.selectionError);
        this.state.variant = variant;
        const nextPreorder = fragment.querySelector('[data-g-preorder]');
        const currentPreorder = this.querySelector('[data-g-preorder]');
        if (!nextPreorder || !currentPreorder) throw new Error(this.labels.selectionError);
        currentPreorder.replaceWith(nextPreorder);
        const refreshedPreorder = nextPreorder.dataset;
        if (acknowledged && oldPreorder.active && refreshedPreorder.active === 'true' && refreshedPreorder.preview !== 'true' && oldPreorder.id === refreshedPreorder.variantId && oldPreorder.window === refreshedPreorder.window) {
          const consent = this.querySelector('[data-preorder-consent]');
          if (consent) consent.checked = true;
        }
        const displayedVariant = variant || (useLocation ? next.variant : null);
        if (displayedVariant) {
          this.querySelector('[data-product-price]').textContent = displayedVariant.price;
          this.querySelector('[data-sticky-price]').textContent = displayedVariant.price;
          const compare = this.querySelector('[data-compare-price]');
          compare.textContent = displayedVariant.comparePrice || '';
          compare.hidden = !displayedVariant.onSale;
        }
        if (variant) {
          this.variantInput.value = variant.id;
          this.quantity.min = variant.minimum;
          this.quantity.step = variant.increment;
          if (variant.maximum) this.quantity.max = variant.maximum;
          else this.quantity.removeAttribute('max');
          if (Number(this.quantity.value) < Number(this.quantity.min)) this.quantity.value = this.quantity.min;
          const mediaIndex = this.slides.findIndex(slide => slide.dataset.mediaId === String(variant.mediaId));
          if (mediaIndex >= 0) this.selectImage(mediaIndex);
        }
        if ((pushHistory || this.suitSelector?.customOrder?.()) && complete) {
          const current = new URL(location.href);
          current.searchParams.delete('variant');
          current.searchParams.delete('option_values');
          current.searchParams.delete('g_pant_waist');
          if (this.suitSelector?.customOrder()) current.searchParams.set('g_pant_waist', this.suitSelector.selected(this.suitSelector.pant));
          if (variant) current.searchParams.set('variant', String(variant.id));
          else current.searchParams.set('option_values', currentOptions.filter(input => input.checked).map(input => input.dataset.optionValueId).join(','));
          history.replaceState({}, '', current);
        }
        this.setFieldError(this.error, '', [this.submit], false);
        const missing = this.optionGroups.find(group => !group.querySelector('input:checked'));
        this.setText(this.querySelector('[data-variant-status]'), !complete ? !this.gTouched ? '' : missing ? this.labels.chooseField.replace('__OPTION__', missing.dataset.optionName) : this.labels.choose : variant?.available ? this.labels.variantAvailable.replace('__PRICE__', variant.price) : this.labels.unavailable);
      } catch (error) {
        if (this.variantAbort === controller && !controller.signal.aborted && error.name !== 'AbortError') {
          this.state.variant = null;
          this.variantInput.value = '';
          this.variantError = true;
          this.setText(this.querySelector('[data-variant-status]'), this.labels.selectionError);
          this.showError(this.labels.selectionError, false);
        }
      } finally {
        if (this.variantAbort === controller) {
          this.resolving = false;
          this.updateControls();
        }
      }
    }
    updateSummary() {
      const selection = this.optionGroups.map(group => {
        const input = group.querySelector('input:checked');
        group.querySelector('[data-option-selection]').textContent = input ? `· ${input.value}` : '';
        return input ? `${group.dataset.optionName} ${input.value}` : '';
      }).filter(Boolean);
      this.suitSelector?.syncProperties?.();
      const properties = new FormData(this.form);
      const preorderKeys = new Set([...this.querySelectorAll('[data-preorder-property],[data-g-custom-pants-property]')].map(input => input.name));
      for (const [key, value] of properties.entries()) {
        if (key.startsWith('properties[') && !key.startsWith('properties[_') && !preorderKeys.has(key) && typeof value === 'string' && value.trim()) selection.push(`${key.slice(11, -1)} ${value}`);
      }
      if (!this.quantity.closest('[hidden]')) selection.push(`${this.labels.quantity} ${this.quantity.value || this.quantity.min}`);
      const text = selection.join(' · ');
      this.setText(this.querySelector('[data-selection-summary]'), text);
      this.setText(this.querySelector('[data-sticky-summary]'), text);
      this.updateStickyClearance();
      this.suitSelector?.refresh();
    }
    updateControls() {
      const pairingNeedsConfirmation = Boolean(this.suitSelector?.requiresConfirmation?.());
      if (pairingNeedsConfirmation) {
        this.state.variant = null;
        this.variantInput.value = '';
      }
      const complete = this.completeSelection();
      const preorder = this.updatePreorder();
      const unavailable = complete && (!this.state.variant || !this.state.variant.available);
      const busy = Boolean(this.submitting || this.resolving || this.externalSubmission);
      const label = this.resolving ? this.labels.optionsLoading : this.submitting || this.externalSubmission ? this.labels.adding : pairingNeedsConfirmation ? this.labels.retryOptions || this.labels.choose : this.variantError ? this.labels.retryOptions || this.labels.choose : !complete ? this.labels.choose : preorder.preview ? this.labels.preorderPreview : unavailable ? this.state.variant ? this.labels.soldOut : this.labels.unavailable : preorder.blocked ? this.labels.preorderBlocked : preorder.active ? this.labels.preorder : this.labels.add;
      this.submitLabel.textContent = `${label}${!busy && complete && !unavailable && this.state.variant ? ` — ${this.state.variant.price}` : ''}`;
      this.stickyButton.textContent = label;
      this.submit.disabled = busy || pairingNeedsConfirmation || preorder.preview || (!this.variantError && complete && preorder.blocked) || (!this.variantError && unavailable) || !this.state.available;
      this.stickyButton.disabled = this.submit.disabled;
      this.submit.setAttribute('aria-busy', String(busy));
      this.form.setAttribute('aria-busy', String(busy));
      if (pairingNeedsConfirmation) this.setText(this.querySelector('[data-variant-status]'), this.labels.selectionError);
      this.querySelector('[data-quantity-change="-1"]').disabled = Number(this.quantity.value) <= Number(this.quantity.min);
      this.querySelector('[data-quantity-change="1"]').disabled = Boolean(this.quantity.max && Number(this.quantity.value) >= Number(this.quantity.max));
      this.updateSummary();
      this.updateSticky();
    }
    preorderInfo() {
      const panel = this.querySelector('[data-g-preorder]');
      const data = panel?.dataset || {};
      const enabled = this.preorderMode === 'confirmed' || this.preorderMode === 'preview' || this.preorderPreview === true;
      const matching = this.completeSelection() && Boolean(this.state.variant) && (!this.suitSelector?.customOrder?.() || this.suitSelector.matchesFulfillment(this.state.variant)) && String(this.state.variant.id) === data.variantId && !this.resolving && !this.variantError;
      const selectedPlan = [...(this.querySelectorAll?.('[name="selling_plan"]') || [])].some(input => !input.disabled && String(input.value || '').trim());
      const requiresPlan = enabled && data.requiresSellingPlan === 'true';
      const planConflict = enabled && matching && data.active === 'true' && selectedPlan;
      return {
        enabled,
        planConflict,
        preview: this.preorderPreview === true || this.preorderMode === 'preview',
        active: enabled && matching && data.active === 'true' && !requiresPlan && !planConflict && this.state.variant.available === true && this.preorderPreview !== true && this.preorderMode === 'confirmed',
        blocked: requiresPlan || planConflict || enabled && (!panel || data.blocked === 'true' || data.mode !== this.preorderMode),
        matching,
        id: data.variantId,
        window: matching && !planConflict ? data.window || '' : '',
        stockLimit: enabled && data.mode === 'confirmed' && matching && Number(data.stockLimit) > 0 ? Number(data.stockLimit) : null
      };
    }
    updatePreorder() {
      const info = this.preorderInfo();
      const panel = this.querySelector('[data-g-preorder]');
      if (!panel) return info;
      const details = panel.querySelector('[data-preorder-details]');
      if (this.suitSelector?.enhanced) this.setText(panel.querySelector('.g-preorder-selection'), this.optionGroups.map(group => `${group.dataset.optionName} ${group.querySelector('input:checked')?.value || ''}`).join(' · '));
      const consent = panel.querySelector('[data-preorder-consent]');
      const error = panel.querySelector('[data-preorder-error]');
      const visible = info.enabled && info.matching && !info.planConflict && panel.dataset.state !== 'none';
      this.setFieldError(this.querySelector('[data-selling-plan-note]'), info.planConflict ? this.labels.preorderOneTimeRequired : '', [...(this.querySelectorAll?.('[name="selling_plan"]') || [])], false);
      if (info.planConflict) {
        if (consent) consent.checked = false;
        this.setText(this.querySelector('[data-variant-status]'), this.labels.preorderOneTimeRequired);
      }
      panel.hidden = !info.preview && !visible;
      if (details) details.hidden = !visible;
      if (consent) {
        consent.disabled = !info.matching || (!info.active && !info.preview);
        consent.required = info.active;
        if (!info.matching) consent.checked = false;
        if (consent.checked || consent.disabled || !visible) this.setFieldError(error, '', [consent]);
      }
      panel.querySelectorAll('[data-preorder-property]').forEach(input => { input.disabled = !info.active || !consent?.checked; });
      const sticky = this.querySelector('[data-sticky-shipping]');
      if (sticky) {
        sticky.textContent = info.window ? this.labels.preorderEstimate.replace('__WINDOW__', info.window) : '';
        sticky.hidden = !info.window;
      }
      if (visible) {
        const estimate = info.window ? this.labels.preorderEstimate.replace('__WINDOW__', info.window) : this.labels.preorderPending;
        this.setText(this.querySelector('[data-variant-status]'), `${info.preview ? this.labels.preorderPreview : info.active ? this.labels.preorder : this.labels.preorderBlocked} · ${estimate}`);
      }
      return info;
    }
    validate() {
      this.suitSelector?.syncProperties?.();
      if (this.suitSelector?.customOrder?.() && !this.suitSelector.matchesFulfillment(this.state.variant)) {this.showError(this.labels.selectionError); return false;}
      if (this.suitSelector?.requiresConfirmation?.()) {
        this.showError(this.labels.selectionError);
        return false;
      }
      let firstInvalid;
      this.optionGroups.forEach(group => {
        const invalid = !group.querySelector('input:checked');
        const error = group.querySelector('[data-option-error]');
        group.classList.toggle('has-error', invalid);
        this.setFieldError(error, invalid ? this.labels.chooseField.replace('__OPTION__', group.dataset.optionName) : '', [group, ...group.querySelectorAll('input')]);
        if (invalid) {
          firstInvalid ||= group.querySelector('input:not(:disabled)');
        }
      });
      const quantityValid = this.quantity.checkValidity() && Number.isSafeInteger(Number(this.quantity.value));
      this.setFieldError(this.querySelector('[data-quantity-error]'), quantityValid ? '' : this.labels.quantityError, [this.quantity]);
      if (!quantityValid) {
        firstInvalid ||= this.quantity;
      }
      if (firstInvalid) {
        this.suitSelector?.reveal(firstInvalid);
        firstInvalid.focus({preventScroll: true});
        firstInvalid.closest('fieldset,.g-product-quantity-row')?.scrollIntoView({block:'center', behavior:'instant'});
        return false;
      }
      if (!this.form.checkValidity()) {
        const field = this.form.querySelector(':invalid');
        if (field) this.suitSelector?.reveal(field);
        this.form.reportValidity(); return false;
      }
      const preorder = this.preorderInfo();
      if (preorder.preview || preorder.blocked) { this.showError(preorder.planConflict ? this.labels.preorderOneTimeRequired : this.labels.preorderBlocked); return false; }
      if (preorder.stockLimit && Number(this.quantity.value) > preorder.stockLimit) { this.showError(this.labels.preorderStock); return false; }
      if (!this.state.variant?.available || !this.variantInput.value) { this.showError(this.labels.unavailable); return false; }
      return true;
    }
    clearAppValidation() {
      this.appValidationEpoch = (this.appValidationEpoch || 0) + 1;
      this.appValidationTicket = null;
      this.appValidationQueued = false;
    }
    nativeOptionsBridgeReady() {
      const app = window.Shoppad?.apps?.infiniteoptions;
      return app?.shopifyCartTransform === true && app.validationEventName === 'click' && window.GiorgioCart?.active === true && typeof window.GiorgioCart.add === 'function' && !this.form.querySelector('input[type="file"]');
    }
    appFormSignature(body = new FormData(this.form)) {
      const entries = [...body.entries()];
      if (entries.some(([, value]) => typeof value !== 'string')) return null;
      return JSON.stringify({variant: String(this.state.variant?.id || ''), entries});
    }
    acceptAppValidation(event) {
      if (!this.nativeOptionsBridgeReady()) return;
      const original = event.detail?.event;
      if (!original || typeof original.preventDefault !== 'function' || original.data?.['io-dynamic-checkout-type']) return;
      const target = original.target;
      if (target?.closest?.('form') !== this.form || !this.submit.contains(target)) return;
      if (original.defaultPrevented || original.isDefaultPrevented?.() || original.originalEvent?.defaultPrevented) return;
      this.appValidationEvents ||= new WeakSet();
      const underlying = original.originalEvent || original;
      if ((typeof underlying !== 'object' && typeof underlying !== 'function') || underlying === null) return;
      if (this.appValidationEvents.has(underlying)) return;
      this.appValidationEvents.add(underlying);
      this.appValidationObserved = true;
      original.preventDefault();
      if (this.appValidationQueued || this.submitting || this.resolving || this.externalSubmission) return;
      this.clearAppValidation();
      const epoch = this.appValidationEpoch;
      this.appValidationQueued = true;
      queueMicrotask(() => {
        if (this.appValidationEpoch !== epoch) return;
        this.appValidationQueued = false;
        if (!this.isConnected || !this.nativeOptionsBridgeReady() || original.isImmediatePropagationStopped?.() || original.isPropagationStopped?.() || this.submitting || this.resolving || this.externalSubmission) { this.clearAppValidation(); return; }
        try {
          const signature = this.appFormSignature();
          if (!signature) throw new Error(this.labels.appOptionsChanged || this.labels.networkError);
          this.appValidationTicket = {signature};
          this.form.requestSubmit(this.submit);
        } catch (error) {
          this.clearAppValidation();
          this.showError(error.message || this.labels.networkError);
        }
      });
    }
    async submitNativeOptions(body) {
      const submittedQuantity = body.get('quantity') || this.quantity.value;
      this.submitting = true;
      this.updateControls();
      this.querySelector('[data-cart-result]').hidden = true;
      try {
        for (const key of [...body.keys()]) if (key.startsWith('giorgio-option-')) body.delete(key);
        const result = await window.GiorgioCart.add(body, this.submit);
        if (!result?.added) throw new Error(result?.error?.message || this.labels.networkError);
        this.querySelector('[data-cart-status]').textContent = this.labels.added.replace('__PRODUCT__', this.state.productTitle).replace('__QUANTITY__', submittedQuantity);
        this.querySelector('[data-cart-result]').hidden = false;
        document.dispatchEvent(new CustomEvent('giorgio:cart-added', {detail:{item:result.item}, bubbles:true}));
      } catch (error) {
        this.showError(error.message || this.labels.networkError);
      } finally {
        this.submitting = false;
        this.updateControls();
      }
    }
    async onSubmit(event) {
      this.gTouched = true;
      if (event.defaultPrevented) return;
      this.suitSelector?.syncProperties?.();
      if (this.submitting || this.resolving || this.externalSubmission) { event.preventDefault(); return; }
      if (this.suitSelector?.requiresConfirmation?.()) { event.preventDefault(); this.clearAppValidation(); this.showError(this.labels.selectionError); return; }
      if (this.variantError) { event.preventDefault(); this.clearAppValidation(); this.resolveVariant(); return; }
      this.setFieldError(this.error, '', [this.submit], false);
      const preorder = this.preorderInfo();
      // Custom waists reserve one real same-jacket suit. Recheck that inventory
      // reference immediately before every submit, including in-stock purchases.
      if (this.suitSelector?.customOrder?.() && !this.preorderSubmissionVerified) {
        event.preventDefault();
        const requested = JSON.stringify(this.suitSelector.fulfillmentPlan());
        await this.resolveVariant(false, false, true);
        const current = this.preorderInfo();
        if (!this.variantError && this.state.variant?.available && this.suitSelector.customOrder() && requested === JSON.stringify(this.suitSelector.fulfillmentPlan()) && !current.preview && !current.blocked && (!current.active || this.querySelector('[data-preorder-consent]')?.checked)) {
          this.preorderSubmissionVerified = true;
          try {this.form.requestSubmit(this.submit);}
          finally {this.preorderSubmissionVerified = false;}
        } else {
          this.clearAppValidation();
          if (current.active && !this.querySelector('[data-preorder-consent]')?.checked) {
            const consent = this.querySelector('[data-preorder-consent]');
            this.setFieldError(this.querySelector('[data-preorder-error]'), this.labels.preorderAcknowledgeError, [consent]);
            consent?.focus();
          }
        }
        return;
      }
      if (preorder.preview || (this.completeSelection() && preorder.blocked)) { event.preventDefault(); this.clearAppValidation(); this.showError(preorder.planConflict ? this.labels.preorderOneTimeRequired : this.labels.preorderBlocked); return; }
      if (preorder.active) {
        if ([...new FormData(this.form).entries()].some(([name, value]) => name === 'selling_plan' && String(value).trim())) {
          event.preventDefault();
          this.clearAppValidation();
          const consent = this.querySelector('[data-preorder-consent]');
          if (consent) consent.checked = false;
          this.querySelector('[data-g-preorder]')?.querySelectorAll('[data-preorder-property]').forEach(input => { input.disabled = true; });
          this.showError(this.labels.preorderOneTimeRequired);
          return;
        }
        const consent = this.querySelector('[data-preorder-consent]');
        if (!consent?.checked) {
          event.preventDefault();
          this.clearAppValidation();
          const error = this.querySelector('[data-preorder-error]');
          this.setFieldError(error, this.labels.preorderAcknowledgeError, [consent]);
          consent?.focus();
          return;
        }
        if (!this.preorderSubmissionVerified) {
          event.preventDefault();
          await this.resolveVariant(false, true, true);
          if (!this.variantError && this.preorderInfo().active && this.querySelector('[data-preorder-consent]')?.checked) {
            this.preorderSubmissionVerified = true;
            try { this.form.requestSubmit(this.submit); }
            finally { this.preorderSubmissionVerified = false; }
          }
          else this.clearAppValidation();
          return;
        }
      }
      this.preorderSubmissionVerified = false;
      if (!this.validate()) { event.preventDefault(); this.clearAppValidation(); return; }
      const appFields = this.querySelector('.g-product-app-options input[name^="properties["],.g-product-app-options select[name^="properties["],.g-product-app-options textarea[name^="properties["]');
      const external = this.dataset.hasAppOptions === 'true' || appFields;
      if (external) {
        if (this.nativeOptionsBridgeReady() && (this.appValidationObserved || appFields)) {
          event.preventDefault();
          const body = new FormData(this.form);
          const signature = this.appFormSignature(body);
          if (!signature || this.appValidationTicket?.signature !== signature) {
            this.clearAppValidation();
            this.showError(this.labels.appOptionsChanged || this.labels.networkError);
            return;
          }
          this.clearAppValidation();
          await this.submitNativeOptions(body);
          return;
        }
        this.clearAppValidation();
        if (event.defaultPrevented) return;
        queueMicrotask(() => {
          if (event.defaultPrevented) return;
          this.externalSubmission = true;
          this.updateControls();
          this.externalTimeout = setTimeout(() => { this.externalSubmission = false; this.updateControls(); }, 12000);
        });
        return;
      }
      event.preventDefault();
      this.submitting = true;
      this.updateControls();
      this.querySelector('[data-cart-result]').hidden = true;
      try {
        const body = new FormData(this.form);
        for (const key of [...body.keys()]) if (key.startsWith('giorgio-option-')) body.delete(key);
        const response = await fetch(this.dataset.cartAdd, {method:'POST', headers:{'Accept':'application/json','X-Requested-With':'XMLHttpRequest'}, body, credentials:'same-origin'});
        const result = await response.json();
        if (!response.ok || result.status) {
          const description = typeof result.description === 'string' ? result.description : this.labels.networkError;
          throw new Error(description);
        }
        this.querySelector('[data-cart-status]').textContent = this.labels.added.replace('__PRODUCT__', this.state.productTitle).replace('__QUANTITY__', this.quantity.value);
        this.querySelector('[data-cart-result]').hidden = false;
        document.dispatchEvent(new CustomEvent('giorgio:cart-added', {detail:{item:result}, bubbles:true}));
        if (window.GiorgioCart?.active) await window.GiorgioCart.afterAdd({opener: this.submit});
        else document.dispatchEvent(new CustomEvent('cart:refresh', {bubbles:true}));
      } catch (error) {
        this.showError(error.message || this.labels.networkError);
      } finally {
        this.submitting = false;
        this.updateControls();
      }
    }
    showError(message, focus = true) {
      this.setFieldError(this.error, message, [this.submit], false);
      if (focus) this.error.focus();
    }
    updateSticky() {
      const footer = document.querySelector('.g-site-footer');
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const footerReached = footer && footer.getBoundingClientRect().top <= viewportHeight;
      const show = this.submit.getBoundingClientRect().bottom < 0 && !footerReached && !this.querySelector('dialog[open]');
      this.sticky.hidden = !show;
      document.body.classList.toggle('g-has-purchasebar', Boolean(show));
      this.updateStickyClearance();
    }
    updateStickyClearance() {
      if (!this.sticky?.hidden) document.documentElement.style.setProperty('--g-purchase-height', `${Math.ceil(this.sticky.getBoundingClientRect().height)}px`);
    }
  }
  customElements.define('giorgio-product', GiorgioProduct);
})();
